import React, { useState, useEffect, useCallback} from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import CustomTablePagination from 'src/shared/TablePagination/CustomTablePagination';
import { GridContainer, Grid } from 'src/shared/grid';
import { Button, Tooltip, TableSortLabel } from '@mui/material';
import { errorResponse} from 'src/store/common';

import 'src/features/data-import/DataImport/DataImport.scss';
import { StyledTableCell, StyledTableRow } from 'src/shared/Styles/Styles';
import { useStoreContext } from 'src/store/context';
import { useAddAlert } from 'src/store/actions';
import RefreshIcon from '@mui/icons-material/Refresh';
import LoadingSpinner from 'src/shared/loading/loading';
import { alertMessages } from 'src/constants';
import { getQueueDownloadJobsStatus } from 'src/apis/getNfdrsCalls';
import DownloadIcon from '@mui/icons-material/Download';

import LinearProgress, { LinearProgressProps } from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import moment from 'moment';
import { visuallyHidden } from '@mui/utils';


function LinearProgressWithLabel(props: LinearProgressProps & { value: number }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '100%', mr: 1 }}>
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary' }}
        >{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  );
}

interface Data {
  id: number;
  jobId: number;
  status: string;
  progress: number;
  queueName: string;
  file_name: string;
  time_started: string;
  completed_time: string;
  errorMessage: string;
}


type Order = 'asc' | 'desc';

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'file_name',
    numeric: false,
    disablePadding: true,
    label: 'FILE',
  },
  {
    id: 'status',
    numeric: false,
    disablePadding: true,
    label: 'STATUS',
  },
    {
    id: 'progress',
    numeric: true,
    disablePadding: true,
    label: 'PROGRESS',
  },
  {
    id: 'time_started',
    numeric: false,
    disablePadding: false,
    label: 'DATE REQUESTED',
  },
  {
    id: 'completed_time',
    numeric: false,
    disablePadding: false,
    label: 'DATE COMPLETED',
  },
];
interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  order: Order;
  orderBy: string;
}

/*SPECIAL SORTABLE HEADER*/
function EnhancedTableHead(props: Readonly <EnhancedTableProps>) {
  const { order, orderBy, onRequestSort } = props;
    const createSortHandler = (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };
  const tzAbbr = new Intl.DateTimeFormat("en-US", {timeZoneName:"short"}).formatToParts(new Date()).find(p=>p.type==="timeZoneName")?.value;
  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell, index) => (
          (
            <TableCell
              className='headerParent'
              style={{backgroundColor: '#d9d9d9', width: '20%!important'}}
              key={headCell.id}
              tabIndex={0}
              align={'left'}
              padding={headCell.disablePadding ? 'none' : 'normal'}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              {
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label?.toLowerCase().includes('date')? `${headCell.label} (${tzAbbr})`: headCell.label}
                  {/* Here we add the sort option + sort arrow */}
                  {orderBy === headCell.id ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : <Box component="span" sx={visuallyHidden}></Box>}

                </TableSortLabel>

              }
            </TableCell>
          )
        ))}
      </TableRow>
    </TableHead>
  );
}

type QueuedDownloadProps={
}

export const QueuedDownload: React.FC<QueuedDownloadProps>= () => {
  const [, dispatch] = useStoreContext();
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof Data>('time_started');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const addAlert = useAddAlert(dispatch);
  
  const [files, setFiles] = useState<any[]>([]);

  const [loading, setLoading] = useState<boolean>(false);

  /**Make a call to populate the table with the bulk data job files */
  const getJobDLResponse = useCallback((request: any) => {
    (async () => {
      try {
        setLoading(true);
        const response: any = await getQueueDownloadJobsStatus(request,addAlert)
        setFiles(response.exportJobStatus.data);
        setTotalCount(response.exportJobStatus._metadata.total_count);

      } catch (error) {
        setFiles([]);
        if(!error?.toString().includes('Guest Users must provide jobIds') && !error?.toString().includes('famauth')){
          errorResponse(error, addAlert, alertMessages.getFiles);
        }
        console.log('error', error);
      }
      setLoading(false);
    })();
  }, [addAlert]); 

  const handleRefresh = useCallback(() => {
    let jobIdsStrings = localStorage.getItem('dlJobs');
    let newJobIdsArrayFromStorage = jobIdsStrings? JSON.parse(jobIdsStrings) : [];
    const request = {
      hideStaleJobs: true,
      jobIds:  newJobIdsArrayFromStorage,
      sortOrder: order,
      sortBy: orderBy,
      perPage:  rowsPerPage,
      page: page,
    }
    getJobDLResponse(request);
  },[ rowsPerPage, page, getJobDLResponse, order, orderBy, ]);

    const handleRequestSort = (event:React.MouseEvent<unknown>, property:keyof Data) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
      setPage(0)
      let jobIdsStrings = localStorage.getItem('dlJobs');
      let newJobIdsArrayFromStorage = jobIdsStrings? JSON.parse(jobIdsStrings) : [];
      const request = {
        hideStaleJobs: true,
        jobIds:  newJobIdsArrayFromStorage,
        sortOrder: isAsc ? 'desc' : 'asc',
        sortBy: property,
        perPage:  rowsPerPage,
        page: page,
      }
      getJobDLResponse(request);
    };

  /**Populate the table */
  useEffect(() => {
    let jobIdsStrings = localStorage.getItem('dlJobs');
    let newJobIdsArrayFromStorage = jobIdsStrings? JSON.parse(jobIdsStrings) : [];
    const request = {
      hideStaleJobs: true,
      jobIds:  newJobIdsArrayFromStorage,
      sortOrder: order,
      sortBy: orderBy,
      perPage:  rowsPerPage,
      page: page,
    }
    getJobDLResponse(request);
  },[getJobDLResponse, page, rowsPerPage, order, orderBy, ]);

  /**Auto refresh the table at a set interval of 45000 Miliseconds */
  useEffect(() => {
    const autoRefreshTable = setInterval(() => {
      handleRefresh();
    }, 45000);
    return () => {
      if(autoRefreshTable){
        clearInterval(autoRefreshTable);
      }
    }
  },[handleRefresh]);
  
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const goToDownload = (file: any)=>{
    const newTab = window.open(`${process.env.REACT_APP_API}/exports/${file.id}/${file.jobId}/download`,'_blank')
    if(newTab){
      window.focus();
    }
  }
  
  
//selected might be where selection is placed
  return (
    <div style={{paddingBottom: '2.5%'}} className='table-page-container' data-testid={'table-page-container'}>

        <h1>Queued Downloads</h1>
        <hr></hr>
          {/*DROP DOWNS AND FILTERS*/}
        <GridContainer className="margin-top-3 dropdown-style" style={{ paddingBottom:'2.5%'}}>
          <Grid row gap="md">
            <div style={{ width: '90%', fontWeight:700, fontSize: '15px'}}>
             <p>Files are available for 24 hours after the download is complete. If you clear your internet cache, the downloads will no longer be available. If a file is no longer available, you may repeat the download process.</p>
            </div>

            <div style={{ width: '2%' }}>
            <Tooltip title={"Refresh Table"} placement="top-start">
                <Button variant="outlined" style={{height: '90%'}} onClick={handleRefresh}><RefreshIcon/></Button>
            </Tooltip>
            </div>
          </Grid>

        </GridContainer>

        <TableContainer component={Paper} className='margin-top-2 first-table MuiPaper-root-special'
          style={{
            width: '100%',
            maxWidth:'95vw',
          }}
        >
        {(loading) && (
          <div style={{height: '30%'}}>
          <LoadingSpinner/>
          </div>
        )}
        {/* TABLE FOR FUELS*/}
          <Table stickyHeader>
              <colgroup>
                <col width="40%" />
                <col width="10%" />
                <col width="10%" />
                <col width="20%" />
                <col width="20%" />
              </colgroup>
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {files.map((row, index) => {
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <StyledTableRow
                    tabIndex={-1}
                    key={row.id}
                    className='selectedHover'
                  >
                    <StyledTableCell className='text-align-override selectedHover'
                      component="th"
                      id={labelId}
                      scope="row"
                      tabIndex={0}
                      padding="none"
                    >
                      {row.status.includes('completed')?
                      <Button variant="text" onClick={()=>goToDownload(row)}><DownloadIcon style={{fontSize: 'large'}}/>{row.fileName}</Button> :row.fileName}
                    </StyledTableCell>
                    <StyledTableCell className='text-align-override' 
                      component="td"
                      tabIndex={0}
                    >
                      {row.status?.toString().toUpperCase()}
                    </StyledTableCell>
                    <StyledTableCell className='text-align-override' 
                      component="td"
                      tabIndex={0}
                    >
                      {
                        <LinearProgressWithLabel value={row.progress} />
                      }
                    </StyledTableCell>
                    <StyledTableCell className='text-align-override'
                      tabIndex={0}

                    >{moment.utc(row.timeStarted).local().format('MM/DD/YYYY HH:mm')}
                    </StyledTableCell>
                    <StyledTableCell className='text-align-override'
                      tabIndex={0}
                    >{row.completedTime? moment.utc(row.completedTime).local().format('MM/DD/YYYY HH:mm'): ''}
                    </StyledTableCell>
                  </StyledTableRow>
                );//
              })}
              {
                files?.length===0&&
                <div style={{padding:'10px', fontSize:'18px'}}>No Queued Downloads</div>
              }
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination
          rowsPerPageOptions={[10, 20, 30]}
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />

    </div>
  );
}